import type {MenuItemProps as MaterialMenuItemProps, MenuItemTypeMap} from '@mui/material/MenuItem';
import MaterialMenuItem from '@mui/material/MenuItem';
import {styled} from '@mui/material/styles';
import React, {ReactNode} from 'react';
import {LinkProps} from '../Link';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface MenuItemProps extends MaterialMenuItemProps<MenuItemTypeMap['defaultComponent'], any> {
    /**
     * Enabling adding href in case component is set to anchor
     */
    href?: LinkProps['href'];
    /**
     * Set Icon Start
     */
    startIcon?: ReactNode;
    /**
     * Set Icon End
     */
    endIcon?: ReactNode;
}

const StyledMenuItem = styled(MaterialMenuItem, {
    ...excludeComponentProps('startIcon', 'endIcon', 'label')
})<MenuItemProps>(({
    theme, ...props
}) => ({
    minWidth: '10rem',
    fontSize: theme.typography.button.fontSize,
    paddingTop: theme.tokens.FuelSpacing2,
    paddingBottom: theme.tokens.FuelSpacing2,
    lineHeight: '1.5rem',

    display: 'grid',
    gridTemplateColumns: props.startIcon ? 'auto 1fr auto' : '1fr auto',
    gridGap: theme.tokens.FuelSpacing2,
    alignItems: 'center',

    '&:active': {
        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultPressed
    },

    '&.Mui-focusVisible': {
        backgroundColor: 'transparent',
        boxShadow: `inset 0 0 0 2px ${theme.tokens.FuelColorGray1000}`
    },
    '&.Mui-selected': {
        color: theme.tokens.FuelColorBrandPrimary
    },
    '&.Mui-selected:before': {
        content: '""',
        position: 'absolute',
        width: '3px',
        height: '100%',
        left: 0,
        top: 0,
        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelectedBar
    },
    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelected
    },
    '&.MuiListItem-root.Mui-selected:active': {
        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelected
    }
}));

const StyledAdornment = styled('span', {
    shouldForwardProp: (prop) => prop !== 'position'
})<{position: 'start' | 'end'}>(({
    theme, position
}) => {
    const iconPaddingOffset = '-2px';

    return {
        display: 'inline-flex',
        [position === 'start' ? 'marginLeft' : 'marginRight']: iconPaddingOffset,

        '& .MuiSvgIcon-root': {
            color: 'inherit',
            fontSize: theme.tokens.FuelFontSizeXl
        }
    };
});

const StyledMenuItemText = styled('span')({
    display: 'inline-block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
});

export const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(({
    children, ...props
}, ref) => {
    const menuItemTitle = typeof children === 'string' ? children : '';

    return (
        <StyledMenuItem
            ref={ref}
            {...props}
        >
            {props.startIcon && <StyledAdornment position='start'>{props.startIcon}</StyledAdornment>}
            <StyledMenuItemText title={menuItemTitle}>{children}</StyledMenuItemText>
            {props.endIcon && <StyledAdornment position='end'>{props.endIcon}</StyledAdornment>}
        </StyledMenuItem>
    );
});

MenuItem.displayName = 'MenuItem';
